"use client";
import { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface AuthContextType {
    isLoggedIn: boolean;
    userEmail: string | null;
    isAuthReady: boolean;
    setLoggedIn: (loggedIn: boolean) => void;
    setUserEmail: (email: string | null) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
    isLoggedIn: false,
    userEmail: null,
    isAuthReady: false,
    setLoggedIn: () => {},
    setUserEmail: () => {},
    logout: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
    const [isLoggedIn, setLoggedInState] = useState<boolean>(() => {
        if (typeof window === "undefined") return false;
        return Boolean(window.localStorage.getItem("access_token"));
    });
    const [userEmail, setUserEmailState] = useState<string | null>(() => {
        if (typeof window === "undefined") return null;
        return window.localStorage.getItem("user_email");
    });
    const [isAuthReady, setIsAuthReady] = useState(false);

    const setLoggedIn = (loggedIn: boolean) => {
        setLoggedInState(loggedIn);
    };

    const setUserEmail = (email: string | null) => {
        setUserEmailState(email);
        if (typeof window !== "undefined") {
            if (email) {
                window.localStorage.setItem("user_email", email);
            } else {
                window.localStorage.removeItem("user_email");
            }
        }
    };

    const logout = () => {
        setLoggedInState(false);
        setUserEmailState(null);
        if (typeof window !== "undefined") {
            window.localStorage.removeItem("access_token");
            window.localStorage.removeItem("user_email");
        }
    };

    useEffect(() => {
        const token = window.localStorage.getItem("access_token");
        const email = window.localStorage.getItem("user_email");
        setLoggedInState(Boolean(token));
        setUserEmailState(email);
        setIsAuthReady(true);
    }, []);

    return (
        <AuthContext.Provider value={{ isLoggedIn, userEmail, isAuthReady, setLoggedIn, setUserEmail, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}